import React, { useState } from 'react'
import { Button } from '@/components/Buttons/Button'
import { NextImage } from '@/components/NextImage'
import { useTranslation } from '@/localization/useTranslation'
import { FirebaseAuth } from '@/actors/AuthActor/Forms/StandardForms/Firebase'
import { FormHeader } from '@/actors/AuthActor/components/FormHeader'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { toSsoAuthError } from '@/actors/AuthActor/utils/toSsoAuthError'
import { AuthErrorType, FormOption, FormChoice } from '@/actors/AuthActor/types'
import { STANDARD_AUTH_FORMS } from '@/constants/auth'
import { useGeolocation } from '@/hooks/geolocation/useGeolocation'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import classes from './style.module.scss'
import classNames from 'classnames'

interface ContainerProps {
  handleNavigateToForm: (mode: string) => void
  compact?: boolean
  form?: FormOption
  /** Scene-wide `options.denyRegister`, forwarded to federated sign-in. */
  denyRegister?: boolean
}

// Type guard to check if form has formChoices
const hasFormChoices = (form: FormOption | undefined): boolean => {
  // Check if it's a container form or a standard form with formChoices
  return (form?.type === 'container' || form?.type === 'standard') &&
    Array.isArray(form?.formChoices) &&
    form.formChoices.length > 0
}

interface AuthOptionProps {
  choice: FormChoice
  handleNavigateToForm: (mode: string) => void
  denyRegister?: boolean
}

export const Container: React.FC<ContainerProps> = (props) => {
  const {
    handleNavigateToForm,
    form,
    denyRegister = false,
  } = props
  const formChoices = hasFormChoices(form) && form?.formChoices ? form.formChoices : []

  return (
    <>
      <FormHeader title={form?.title} subtitle={form?.subtitle} />
      <div className={classes.options}>
        {formChoices?.map((choice, index) => (
          <AuthOption
            key={`${choice?.form}-${index}`}
            choice={choice}
            handleNavigateToForm={handleNavigateToForm}
            denyRegister={denyRegister}
          />
        ))}
      </div>
    </>
  )
}

const AuthOption: React.FC<AuthOptionProps> = (props) => {
  const { choice, handleNavigateToForm, denyRegister = false } = props
  const { t } = useTranslation()
  const { sendGtm } = useTagManager()
  // Federated sign-in has no mutation state of its own to read here: `useAuth`
  // mints a fresh observer per call, so the rejection has to travel up from
  // `FirebaseAuth` rather than be read off `firebaseSsoMutation.error` the way
  // `Login` reads its own. Kept local to the choice that can raise it.
  const [ssoError, setSsoError] = useState<AuthErrorType | null>(null)
  const { isAllowed, isLoading } = useGeolocation({
    allow: choice?.geolocation?.allow || [],
    deny: choice?.geolocation?.deny || []
  })

  if (!isAllowed && !isLoading) {
    return null
  }

  const {
    form,
    label,
    button = {
      text: '',
      variant: '',
      icon: '',
    },
    methods = []
  } = choice
  const { text, variant, icon } = button

  const handleSsoError = (error: unknown) => {
    setSsoError(toSsoAuthError(error))
  }

  const handleSsoErrorRedirect = (path: string) => {
    setSsoError(null)
    handleNavigateToForm(path)
  }

  const handleTrigger = () => {
    sendGtm.USER_SSO_FLOW_STARTED?.({
      provider: form,
      path: window.location.pathname,
    })

    handleNavigateToForm?.(form)
  }

  if (form === STANDARD_AUTH_FORMS.FIREBASE) {
    return (
      <div className={classNames({
        [classes.option]: true,
        [classes['option--loading']]: isLoading,
      })}>
        <div className={classes.label}>
          {t(label || 'Login or register with')}
        </div>
        <FirebaseAuth
          methods={methods}
          denyRegister={denyRegister}
          onError={handleSsoError}
        />
        <AuthErrorModal
          error={ssoError as AuthErrorType}
          handleError={() => setSsoError(null)}
          handleRedirect={handleSsoErrorRedirect}
        />
      </div>
    )
  }

  if (form === STANDARD_AUTH_FORMS.REGISTER) {
    return (
      <div className={classNames({
        [classes.option]: true,
        [classes['option--loading']]: isLoading,
      })}>
        <div className={classes.label}>
          {t(label || "Don't have an account?")}
        </div>
        <Button
          variant={variant || 'tertiary'}
          onClick={() => handleNavigateToForm(STANDARD_AUTH_FORMS.REGISTER)}
          fullWidth
          data-testid="auth-form-register-button"
        >
          {icon && <NextImage src={icon} alt="register button image" width={24} height={24} />}
          {t(text || 'Register')}
        </Button>
      </div>
    )
  }

  if (form === STANDARD_AUTH_FORMS.LOGIN) {
    return (
      <div className={classNames({
        [classes.option]: true,
        [classes['option--loading']]: isLoading,
      })}>
        <div className={classes.label}>{t(label || 'Or continue with email')}</div>

        <Button
          variant={variant || 'primary'}
          onClick={() => handleNavigateToForm(STANDARD_AUTH_FORMS.LOGIN)}
          fullWidth
          data-testid="auth-login-email-button"
        >
          {icon && <NextImage src={icon} alt="login button image" width={24} height={24} />}
          {t(text || 'Log in')}
        </Button>
      </div>
    )
  }

  return (
    <div className={classNames({
      [classes.option]: true,
      [classes['option--loading']]: isLoading,
    })}>
      {label && <div className={classes.label}>{t(label)}</div>}
      <Button
        variant={variant || 'secondary'}
        onClick={handleTrigger}
        fullWidth
        data-testid={'auth-login-general-button'}
      >
        {icon && <NextImage src={icon} alt="auth button option" width={24} height={24} />}
        {t(text)}
      </Button>
    </div>
  )
}